import { LoadingBlockFullPage } from '@/components/loading/loading-block-full-page';
import { CopyButton } from '@/components/ui/buttons/copy-button';
import { DrawerContainer, DrawerContent, DrawerHeader } from '@/components/ui/drawer';
import { TabsHeader } from '@/components/ui/tabs-header';
import { useAppConfig } from '@/features/acl/acl-provider';
import { Drawer404 } from '@/features/drawers/drawer-404';
import { OrderStatusBadge } from '@/features/statuses/components/order-status-badge';
import { useLocale } from '@/lib/hooks/use-locale';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import React, { useState } from 'react';
import { QuoteOrderActionDropdownMenu } from '../components/quote-order-action-dropdown-menu';
import { QuoteOrderAttachmentsTab } from '../components/quote-order-attachments-tab';
import { QuoteOrderDetailTab } from '../components/quote-order-detail-tab';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order';

enum PageTabs {
    DETAILS = 'details',
    ATTACHMENTS = 'attachments',
}

interface Props {
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    type: SrSalesOrderType;
    transactionId: string;
}

export const QuoteOrderDetailPage: React.FC<Props> = ({
    onBack,
    onClose,
    className,
    transactionId,
    type,
}) => {
    const { t } = useLocale();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.DETAILS);

    const { canViewSalesOrderDocuments } = useAppConfig();

    const { data, isLoading } = useDetailQuoteOrder({ transactionId });

    if (isLoading) {
        return <LoadingBlockFullPage />;
    }

    if (!data) {
        return <Drawer404 onBack={onBack} onClose={onClose} />;
    }

    const { header } = data;

    const title = `${
        type === SrSalesOrderType.ORDER
            ? t('order', { ns: 'transaction-details' })
            : t('quote', { ns: 'transaction-details' })
    } ${header.display_identifier}`;

    return (
        <DrawerContainer className={className}>
            <div className="flex flex-col gap-3 bg-white">
                <DrawerHeader
                    className="border-b-0 pb-0"
                    onBack={onBack}
                    onClose={onClose}
                    title={title}
                    actions={
                        <>
                            {header.display_identifier && (
                                <CopyButton
                                    textToCopy={header.display_identifier}
                                    className="hidden sm:inline-flex"
                                />
                            )}
                            <QuoteOrderActionDropdownMenu
                                customerId={header.customer_id}
                                type={type}
                                transactionId={transactionId}
                                onDelete={onClose ?? onBack}
                                displayIdentifier={header.display_identifier}
                            />
                        </>
                    }
                >
                    <OrderStatusBadge status={header.status} type={header.type} className="ml-0.5" />
                </DrawerHeader>
                <TabsHeader<PageTabs>
                    className="px-4"
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => setSelectedTab(value)}
                    tabs={[PageTabs.DETAILS, ...(canViewSalesOrderDocuments ? [PageTabs.ATTACHMENTS] : [])]}
                    getTabLabel={(tab) => t(`tabs__${tab}`, { ns: 'transaction-details' })}
                />
            </div>
            <DrawerContent>
                {selectedTab === PageTabs.DETAILS && (
                    <QuoteOrderDetailTab data={data} type={type} isLoading={isLoading} />
                )}
                {selectedTab === PageTabs.ATTACHMENTS && (
                    <QuoteOrderAttachmentsTab transactionId={transactionId} />
                )}
            </DrawerContent>
        </DrawerContainer>
    );
};
